import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useLocation } from 'react-router';
import { Body5, Body7, Container, H5 } from '@ui';
import { TextField } from '@ui/inputs';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { ROUTES } from '@constants/routes';
import { useSpecEditor, nextVersionSeed } from './useSpecEditor';
import { specDisplayName, formatStatus, formatSource, formatDateTime, formatUser } from './format';
import { SpecStatusBadge, LiveBadge } from './SpecStatusBadge';
import { SpecLifecycleBar } from './SpecLifecycleBar';
import { SpecEditorProvider, SpecEditorContext } from './SpecEditorContext';
import { QuestionPanel } from './QuestionPanel';
import { ContractRail } from './ContractRail';
import { ProblemsStrip } from './ProblemsStrip';
import { UnknownRootKeysStrip } from './UnknownRootKeysStrip';
import { WhenPanel } from './WhenPanel';
import { WhatPanel } from './WhatPanel';
import { Studio } from './Studio';
import { OutPanel } from './OutPanel';
import { SUMMARIZERS } from './summary';
import { countErrors, countSaveBlockers, PROBLEM_PANELS } from './validate';
import { SPEC_ID_REGEX } from './specSchema';
import { diffPaths } from './diffPaths';
import { toPayload } from './payload';
import { SCORE_SPEC_HEADING_ID } from './headingId';
import { Consequence, MicroLabel, PassiveCard, Row } from './styles';
import { SpecJsonPane } from './SpecJsonPane';

// The null label is the caller's to supply, and the two audit fields do NOT share one: a null
// created_by means the spec was seeded, while a null approved_by just means nobody has approved it.
const userLabel = (id, absentLabel) => (id == null ? absentLabel : formatUser(id));

// ---- detail (view) styling ----
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DefList = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 20px;
  margin: 0;
`;

const Dt = styled.dt`
  font-size: 12px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

const Dd = styled.dd`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.themeColors.text};
`;

const SectionHead = ({ heading, subtext }) => (
  <>
    <Body5 bold>{heading}</Body5>
    {subtext && (
      <Container top={2}>
        <Body7 isLowContrast>{subtext}</Body7>
      </Container>
    )}
  </>
);

const DefinitionListBlock = ({ rows }) => (
  <DefList>
    {rows.map((r) => (
      <React.Fragment key={r.label}>
        <Dt>{r.label}</Dt>
        <Dd>{r.value}</Dd>
      </React.Fragment>
    ))}
  </DefList>
);

// Read-only record page for an already-created spec. Reads top-to-bottom: what it is, its state
// in one sentence, its provenance/timeline, the frozen definition, and finally what to do next.
const SpecDetail = ({ spec, onChanged, onDuplicate }) => {
  const aboutRows = [
    { label: 'Spec ID', value: spec.score_spec_id },
    { label: 'Version', value: `v${spec.version}` },
    { label: 'Status', value: formatStatus(spec.status) },
    { label: 'Source', value: formatSource(spec.source) },
  ];
  if (spec.status === 'approved' || spec.status === 'retired') {
    aboutRows.push({ label: 'Live firing', value: spec.live_enabled ? 'On' : 'Off' });
  }
  aboutRows.push({ label: 'Created by', value: userLabel(spec.created_by, 'Seed (system)') });
  if (spec.approved_by != null) aboutRows.push({ label: 'Approved by', value: formatUser(spec.approved_by) });

  const timelineRows = [{ label: 'Created', value: formatDateTime(spec.created_at) }];
  if (spec.locked_at) timelineRows.push({ label: 'Locked', value: formatDateTime(spec.locked_at) });
  if (spec.approved_at) timelineRows.push({ label: 'Approved', value: formatDateTime(spec.approved_at) });
  if (spec.retired_at) timelineRows.push({ label: 'Retired', value: formatDateTime(spec.retired_at) });
  timelineRows.push({ label: 'Last updated', value: formatDateTime(spec.updated_at) });

  return (
    <div>
      <TitleRow>
        <H5 id={SCORE_SPEC_HEADING_ID} tabIndex={-1}>
          {specDisplayName(spec)}
        </H5>
        <SpecStatusBadge status={spec.status} />
        {spec.live_enabled && <LiveBadge live />}
      </TitleRow>
      <Container top={4}>
        <Body7 isLowContrast>
          {formatSource(spec.source)} · v{spec.version}
        </Body7>
      </Container>

      <Container top={24}>
        <SectionHead heading="Details" subtext="Times are UTC." />
        <Container top={16}>
          <DefinitionListBlock rows={[...aboutRows, ...timelineRows]} />
        </Container>
      </Container>

      <Container top={24}>
        <SectionHead
          heading="Definition"
          subtext="The authored definition, frozen at lock — read-only. To change it, duplicate this spec as a new version."
        />
        {spec.spec_json?.description && (
          <Container top={16}>
            <Body5>{spec.spec_json.description}</Body5>
          </Container>
        )}
        <Container top={16}>
          {/* Two honest answers to "the JSON of this spec", and the difference matters when you are
              reading a frozen one: the definition is what the compiler was handed, the record is
              that plus the operational columns the definition never carries. */}
          <SpecJsonPane
            testId="spec-json"
            defaultOpen
            views={[
              {
                key: 'definition',
                label: 'Definition',
                value: spec.spec_json,
                caption: 'The authored definition — exactly the spec_json the compiler reads. Frozen at lock.',
              },
              {
                key: 'record',
                label: 'Whole record',
                value: spec,
                caption:
                  'The definition plus the columns around it: status, live firing, who created and approved it, and when. This is what GET /score_specs/:id returns.',
              },
            ]}
          />
        </Container>
      </Container>

      <Container top={24}>
        <SectionHead heading="Actions" />
        <Container top={16}>
          <SpecLifecycleBar spec={spec} onChanged={onChanged} onDuplicate={onDuplicate} />
        </Container>
      </Container>
    </div>
  );
};

// The identity strip.
//
// `version` has to equal the `_vN` suffix or the spec will not compile, and the obvious
// implementation — derive it and write it — is wrong twice over. It would write the buffer on
// MOUNT, so merely opening a spec whose version disagrees would mark it dirty and quietly change a
// field the operator never touched. And a read-only box showing the derived number while the buffer
// holds a different one is a display that lies about what will be saved.
//
// So: the field shows what is actually in the buffer, says plainly when it disagrees with the
// suffix, and offers the repair as a button the operator presses.
const IdentityStrip = () => {
  const { definition, setValue } = React.useContext(SpecEditorContext);

  const id = definition.score_spec_id || '';
  const match = SPEC_ID_REGEX.exec(id);
  const derivedVersion = match ? Number(match[1]) : null;
  const mismatched = derivedVersion !== null && Number(definition.version) !== derivedVersion;

  return (
    <div>
      <Row $align="flex-start">
        <div style={{ flex: '2 1 260px' }}>
          <TextField
            label="Score spec id"
            mono
            value={id}
            onChange={(v) => setValue('score_spec_id', v)}
            placeholder="share_buyback_mentions_v1"
            invalid={!!id && !match}
            hint={
              match
                ? 'Lowercase, ending in _vN. Changing the suffix changes the version below.'
                : 'Must be lowercase letters, digits and underscores, ending in _v followed by a number.'
            }
          />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <TextField
            label="Version"
            mono
            readOnly
            value={definition.version === undefined || definition.version === null ? '' : definition.version}
            invalid={mismatched}
            hint={mismatched ? `The id ends in _v${derivedVersion}.` : 'Set by the _vN suffix on the id.'}
          />
          {mismatched && (
            <Container top={6}>
              <TransparentFlatButton onClick={() => setValue('version', derivedVersion)}>
                {`Set version to ${derivedVersion}`}
              </TransparentFlatButton>
            </Container>
          )}
        </div>
      </Row>

      <Container top={16}>
        <TextField
          label="Description"
          value={definition.description || ''}
          onChange={(v) => setValue('description', v)}
          invalid={!definition.description}
          hint="One line. This is what the spec list and the backfill picker show."
        />
      </Container>

      <Container top={16}>
        <TextField
          label="Notes"
          multiline
          rows={2}
          value={definition.notes || ''}
          onChange={(v) => setValue('notes', v)}
          hint="Optional rationale or caveats."
        />
      </Container>
    </div>
  );
};

const ChangedStrip = () => {
  const { definition, seedOrigin } = React.useContext(SpecEditorContext);
  if (!seedOrigin) return null;

  const paths = diffPaths(definition, seedOrigin);
  return (
    <PassiveCard>
      <MicroLabel>{`Changed from ${seedOrigin.score_spec_id || 'the spec this came from'} · ${paths.length} path${
        paths.length === 1 ? '' : 's'
      }`}</MicroLabel>
      <Container top={6}>
        {paths.length === 0 ? (
          <Body7 isLowContrast>Nothing differs yet — this is a byte-identical copy.</Body7>
        ) : (
          <Body7 isLowContrast>{paths.join(' · ')}</Body7>
        )}
      </Container>
    </PassiveCard>
  );
};

// The draft editor: four questions over the four authored sub-objects, a rail that shows what each
// stage hands the next, and one list of everything that would fail the compile.
const DraftEditor = ({ editor, specId, isCreate, onDuplicate, onSave }) => {
  const { spec, definition, savedDefinition, isDirty, isSaving, error, revertEdits, reload, revertSection } = editor;

  const { problems } = React.useContext(SpecEditorContext);
  const errors = countErrors(problems);
  // Counted apart because they fail at different doors: a save blocker is a 422 on the next Save, a
  // lock error is a compile refusal at the irreversible one.
  const saveBlockers = countSaveBlockers(problems);

  return (
    <div>
      <Container bottom={16}>
        <TitleRow>
          <H5 id={SCORE_SPEC_HEADING_ID} tabIndex={-1}>
            {isCreate ? 'New score spec' : specDisplayName(spec)}
          </H5>
          {spec && <SpecStatusBadge status={spec.status} />}
          {isDirty && <Body7 isLowContrast>Unsaved edits</Body7>}
        </TitleRow>
      </Container>

      <ChangedStrip />

      <Container top={16}>
        <IdentityStrip />
      </Container>

      <Container top={16}>
        <UnknownRootKeysStrip />
      </Container>

      <Container top={24}>
        <ContractRail />
      </Container>

      <Container top={24}>
        <QuestionPanel
          index={1}
          panel={PROBLEM_PANELS.TRIGGER}
          sectionKey="trigger"
          title="When does it run?"
          summary={SUMMARIZERS.trigger(definition)}
          onRevertSection={() => revertSection('trigger')}
        >
          <WhenPanel />
        </QuestionPanel>

        <QuestionPanel
          index={2}
          panel={PROBLEM_PANELS.DOCUMENT_SET}
          sectionKey="document_set"
          title="What does the model see?"
          summary={SUMMARIZERS.document_set(definition)}
          onRevertSection={() => revertSection('document_set')}
        >
          <WhatPanel />
        </QuestionPanel>

        <QuestionPanel
          index={3}
          panel={PROBLEM_PANELS.SCORE_GEN}
          sectionKey="score_gen"
          title="How is it asked?"
          summary={SUMMARIZERS.score_gen(definition)}
          onRevertSection={() => revertSection('score_gen')}
        >
          <Studio specId={specId} isCreate={isCreate} isDirty={isDirty} />
        </QuestionPanel>

        <QuestionPanel
          index={4}
          panel={PROBLEM_PANELS.OUTPUT}
          sectionKey="output_extraction"
          title="What comes out?"
          summary={SUMMARIZERS.output_extraction(definition)}
          onRevertSection={() => revertSection('output_extraction')}
        >
          <OutPanel />
        </QuestionPanel>
      </Container>

      <Container top={24}>
        <ProblemsStrip />
      </Container>

      {error && (
        <Container top={16}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}

      <Container top={24}>
        <Row>
          <TransparentFlatButton
            onClick={() => !isSaving && isDirty && revertEdits()}
            disabled={!isDirty || isSaving}
            buttonProps={{ 'aria-disabled': !isDirty || isSaving || undefined }}
          >
            Revert all
          </TransparentFlatButton>
          <FlatButton
            onClick={() => !isSaving && onSave()}
            disabled={isSaving}
            buttonProps={{ 'aria-disabled': isSaving || undefined }}
          >
            {isSaving ? 'Saving…' : 'Save draft'}
          </FlatButton>
        </Row>
        <Container top={8}>
          <Consequence>
            This editor never blocks Save. The server accepts an incomplete draft on purpose so you can stop mid-thought
            — but it does reject a few things outright, and those rows are tagged `save`. Locking is what runs the real
            compiler, and locking is permanent.
          </Consequence>
        </Container>
      </Container>

      {spec && (
        <Container top={24}>
          <SpecLifecycleBar
            spec={spec}
            onChanged={reload}
            onDuplicate={onDuplicate}
            problemCount={errors}
            saveBlockerCount={saveBlockers}
            isDirty={isDirty}
          />
        </Container>
      )}

      {/* Last on the page, and read-only. The four panels above show the spec through a form; this
          shows the document. Three named views rather than one dump, because on a DRAFT "the JSON
          of this spec" has more than one true answer and the editor has spent four panels being
          careful about exactly that distinction — the buffer is not the payload (payload.js strips
          keys the format has dropped), and neither is what the server currently holds. The saved
          view only appears when it would differ from what is on screen. */}
      <Container top={24}>
        <SpecJsonPane
          views={[
            {
              key: 'buffer',
              label: 'On screen',
              value: definition,
              caption:
                'The editing buffer as it stands, including anything typed since the last save. This is what the panels above are showing.',
            },
            {
              key: 'payload',
              label: 'What Save sends',
              value: toPayload(definition),
              caption:
                'The buffer with the keys the format has dropped removed. A legacy row still carries them and still shows them above; they are not sent, so they die on the next save.',
            },
            isDirty && spec
              ? {
                  key: 'saved',
                  label: 'Last saved',
                  value: savedDefinition,
                  caption:
                    'What the server holds right now. There are unsaved edits, so this is not what is on screen — and it is what a preview run compiles.',
                }
              : null,
          ]}
        />
      </Container>
    </div>
  );
};

export const SpecEditor = () => {
  const { specId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const editor = useSpecEditor(specId, state?.seed);

  const { spec, isDraft, isLoading, definition, error, setValue, removeValue, replaceSection, seedOrigin } = editor;

  const isCreate = specId === 'new';

  // Duplicating remounts the editor, so the in-memory buffer is gone. Seeding from the SAVED row
  // would silently drop whatever the operator has typed since; seeding from the live buffer carries
  // it into the new version, which is what "duplicate this" means when you are looking at edits.
  const onDuplicate = () =>
    navigate(`${ROUTES.scoreSpecSpecs}/new`, {
      state: { seed: nextVersionSeed(editor.isDirty ? definition : spec.spec_json) },
    });

  // A create that succeeds becomes an edit of the record the server just made, so the URL has to
  // follow it — otherwise the next save POSTs again and 409s on the id it just created.
  const onSave = async () => {
    const savedId = await editor.save();
    if (savedId && isCreate) navigate(`${ROUTES.scoreSpecSpecs}/${savedId}`);
  };

  if (isLoading) {
    return <Body5 isLowContrast>Loading score spec…</Body5>;
  }

  // A failed load leaves both `spec` and `definition` null. Rendering onward from here dereferences
  // the record and throws inside the app-wide ErrorBoundary, which replaces the ENTIRE app with a
  // generic failure page — so the one message that explains what happened is the thing that
  // disappears. Show the error instead.
  if (!spec && !isCreate) {
    return (
      <div>
        <H5 id={SCORE_SPEC_HEADING_ID} tabIndex={-1}>
          Score spec
        </H5>
        <Container top={16}>
          <ErrorText>{error || 'That spec could not be loaded.'}</ErrorText>
        </Container>
        <Container top={16}>
          <FlatButton onClick={() => navigate(ROUTES.scoreSpec)}>Back to specs</FlatButton>
        </Container>
      </div>
    );
  }

  // Locked / approved / retired — frozen. The read-only record page.
  if (!isDraft) {
    return <SpecDetail spec={spec} onChanged={editor.reload} onDuplicate={onDuplicate} />;
  }

  return (
    <SpecEditorProvider
      definition={definition}
      setValue={setValue}
      removeValue={removeValue}
      replaceSection={replaceSection}
      seedOrigin={seedOrigin}
      bufferEpoch={editor.bufferEpoch}
      savedEpoch={editor.savedEpoch}
    >
      <DraftEditor editor={editor} specId={specId} isCreate={isCreate} onDuplicate={onDuplicate} onSave={onSave} />
    </SpecEditorProvider>
  );
};
